/**
 * iter87.213.4 / iter87.304 — MLB Playoffs (Highlightly) poller cadence card.
 *
 * Mirrors the NFL cadence card: live-window aware — while any unplayed
 * game is in progress (or <1h from first pitch) the poller wakes on the
 * LIVE interval instead of the dark one. Manual "Run Now" + per-
 * tournament last-synced-at / live / stale list (the vendor-onboarding
 * workflow's "surface last_synced_at" + "manual refresh" requirements).
 */
import { useEffect, useState } from "react";
import { RefreshCw, Save, RotateCcw, Play } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const fmtSec = (n) => {
    if (n == null) return "—";
    if (n < 60) return `${n}s`;
    if (n < 3600) return `${Math.round(n / 60)}m`;
    return `${Math.round(n / 3600)}h`;
};

const fmtAgo = (iso) => {
    if (!iso) return "never";
    const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
    return `${fmtSec(s)} ago`;
};

const FIELDS = [
    {
        key: "poll_interval_sec", label: "Dark poll interval (sec)",
        help: "How often the poller wakes when no game is live. Floor 120s.",
        testid: "mlb-cadence-interval",
    },
    {
        key: "live_poll_interval_sec", label: "LIVE poll interval (sec)",
        help: "Wake interval while a game is in progress or <1h from first pitch. Floor 60s.",
        testid: "mlb-cadence-live-interval",
    },
];

export default function MlbPollerCadenceCard() {
    const [data, setData] = useState(null);
    const [status, setStatus] = useState(null);
    const [draft, setDraft] = useState({ poll_interval_sec: "", live_poll_interval_sec: "" });
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [running, setRunning] = useState(false);

    const reload = async () => {
        setLoading(true);
        try {
            const [d, s] = await Promise.all([api.mlbGetPollerCadence(), api.mlbGetPollerStatus()]);
            setData(d);
            setStatus(s);
            const next = {};
            for (const f of FIELDS) next[f.key] = d.overrides.includes(f.key) ? String(d[f.key]) : "";
            setDraft(next);
        } catch {
            toast.error("Failed to load MLB poller cadence");
        } finally { setLoading(false); }
    };

    useEffect(() => { reload(); }, []);

    const onSave = async () => {
        setSaving(true);
        try {
            const body = {};
            for (const f of FIELDS) {
                const v = (draft[f.key] || "").toString().trim();
                body[f.key] = v === "" ? null : Number(v);
                if (body[f.key] !== null && !Number.isInteger(body[f.key])) {
                    toast.error(`${f.key} must be a positive integer`);
                    setSaving(false); return;
                }
            }
            const d = await api.mlbUpdatePollerCadence(body);
            setData(d);
            toast.success("MLB cadence saved — applies on the next poller wake");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally { setSaving(false); }
    };

    const onRunNow = async () => {
        setRunning(true);
        try {
            const r = await api.mlbPollerRunNow();
            toast.success(`Synced ${r.synced}/${r.total} MLB tournament${r.total === 1 ? "" : "s"}${r.failed ? ` (${r.failed} failed)` : ""}`);
            reload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Run Now failed");
        } finally { setRunning(false); }
    };

    const eff = (key) => (draft[key] ? Number(draft[key]) : (data?.defaults?.[key] || 0));
    const liveInterval = eff("live_poll_interval_sec");
    // Rough upstream-call estimate (real vendor calls are further
    // damped by Highlightly's own 3min games cache — this is a
    // ceiling, not the actual count): ~8h/day of live-window games.
    const darkCalls = eff("poll_interval_sec") ? Math.round((16 * 3600) / eff("poll_interval_sec")) : null;
    const liveCalls = liveInterval ? Math.round((8 * 3600) / liveInterval) : null;

    return (
        <div className="brutal-card p-4" data-testid="mlb-poller-cadence-card">
            <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-100 flex items-center gap-2">
                    ⚾ MLB Playoffs (Highlightly) Poller Cadence
                </h3>
                <div className="flex items-center gap-3">
                    <button
                        onClick={onRunNow}
                        disabled={running}
                        className="text-xs text-neon hover:text-neon/80 flex items-center gap-1"
                        data-testid="mlb-poller-run-now"
                    >
                        <Play className={`w-3 h-3 ${running ? "animate-pulse" : ""}`} /> {running ? "Running…" : "Run Now"}
                    </button>
                    <button
                        onClick={reload}
                        disabled={loading}
                        className="text-xs text-zinc-400 hover:text-white flex items-center gap-1"
                        data-testid="mlb-poller-cadence-refresh"
                    >
                        <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Reload
                    </button>
                </div>
            </div>

            {!data ? (
                <p className="text-xs text-zinc-500">Loading…</p>
            ) : (
                <>
                    <p className="text-xs text-zinc-400 mb-4">
                        Tune how often the poller re-syncs MLB Playoffs tournaments from Highlightly
                        (teams, series scores, bracket advancement). Blank = env-var default. The poller is{" "}
                        <strong className="text-zinc-200">live-window aware</strong>: while any unplayed
                        game is in progress (or &lt;1h from first pitch) it uses the LIVE interval;
                        otherwise it uses the dark interval. Changes apply on the <strong>next</strong> wake.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                        {FIELDS.map((f) => (
                            <div className="space-y-1" key={f.key}>
                                <div className="flex items-center justify-between">
                                    <label className="text-[10px] uppercase tracking-wider text-zinc-400">{f.label}</label>
                                    {data.overrides.includes(f.key) && (
                                        <button
                                            onClick={() => setDraft(p => ({ ...p, [f.key]: "" }))}
                                            title="Revert to default"
                                            className="text-[9px] text-blaze hover:text-blaze/80 flex items-center gap-1"
                                            data-testid={`${f.testid}-revert`}
                                        >
                                            <RotateCcw className="w-2.5 h-2.5" /> revert
                                        </button>
                                    )}
                                </div>
                                <input
                                    type="number"
                                    min="1"
                                    value={draft[f.key]}
                                    placeholder={String(data.defaults[f.key])}
                                    onChange={(e) => setDraft(p => ({ ...p, [f.key]: e.target.value }))}
                                    className="w-full px-2 py-1 bg-zinc-950 border border-zinc-800 rounded text-xs font-mono text-white focus:border-neon focus:outline-none"
                                    data-testid={f.testid}
                                />
                                <p className="text-[10px] text-zinc-500">
                                    {f.help} Current: <span className="font-mono text-zinc-300">{data[f.key]}</span>
                                    {data.overrides.includes(f.key)
                                        ? <span className="text-blaze ml-1">(override)</span>
                                        : <span className="text-zinc-600 ml-1">(default)</span>}
                                </p>
                            </div>
                        ))}
                    </div>

                    {darkCalls !== null && (
                        <div className="text-xs text-zinc-400 mb-4 p-2 bg-zinc-950/50 border border-zinc-800 rounded">
                            <strong className="text-zinc-200">Estimated upstream calls:</strong>{" "}
                            <span className="font-mono text-neon">~{darkCalls + (liveCalls || 0)}/day</span>{" "}
                            <span className="text-zinc-500">
                                (dark wake every {fmtSec(eff("poll_interval_sec"))} + ~8h/day live @ {fmtSec(liveInterval)}) — actual
                                vendor hits are lower thanks to Highlightly's own 3min games cache.
                            </span>
                        </div>
                    )}

                    <button
                        onClick={onSave}
                        disabled={saving}
                        className="px-3 py-1.5 bg-neon/20 border border-neon text-neon rounded text-xs font-bold uppercase tracking-wider flex items-center gap-2 hover:bg-neon/30 disabled:opacity-50 mb-4"
                        data-testid="mlb-poller-cadence-save"
                    >
                        <Save className="w-3 h-3" /> {saving ? "Saving…" : "Save Cadence"}
                    </button>

                    <div className="text-xs text-zinc-400 p-2 bg-zinc-950/50 border border-zinc-800 rounded" data-testid="mlb-poller-status-list">
                        <strong className="text-zinc-200">Last synced (per tournament):</strong>
                        {!status?.tournaments?.length ? (
                            <p className="text-zinc-500 mt-1">No MLB Playoffs tournaments yet.</p>
                        ) : (
                            <ul className="mt-1 space-y-0.5">
                                {status.tournaments.map((t) => (
                                    <li key={t.tournament_id} data-testid={`mlb-poller-status-${t.tournament_id}`} className="flex justify-between gap-2">
                                        <span className="text-zinc-300 truncate flex items-center gap-1">
                                            {t.name || t.tournament_id}
                                            {t.is_live && <span className="text-neon text-[9px] uppercase" data-testid={`mlb-poller-live-${t.tournament_id}`}>● live</span>}
                                            {t.is_stale && <span className="text-blaze text-[9px] uppercase" data-testid={`mlb-poller-stale-${t.tournament_id}`}>⚠ stale</span>}
                                            {!t.is_stale && t.is_complete && <span className="text-zinc-400 text-[9px] uppercase" data-testid={`mlb-poller-complete-${t.tournament_id}`}>✓ complete</span>}
                                        </span>
                                        <span className="font-mono text-zinc-500 shrink-0">{fmtAgo(t.last_synced_at)}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}
